import { useEffect, useState } from 'react';

import { NoticePopup } from './types';

const HIDE_FOREVER_VALUE = 'true';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const readHideValue = (storageKey: string): string | null => {
  try {
    return localStorage.getItem(storageKey);
  } catch (error) {
    console.error('팝업 숨김 정보 조회 실패:', error);
    return null;
  }
};

const writeHideValue = (storageKey: string, value: string) => {
  try {
    localStorage.setItem(storageKey, value);
  } catch (error) {
    console.error('팝업 숨김 정보 저장 실패:', error);
  }
};

const isHiddenByUser = (popup: NoticePopup, now: number): boolean => {
  const hideValue = readHideValue(popup.storageKey);
  if (!hideValue) return false;

  // '다시 보지 않기' → 영구 숨김
  if (hideValue === HIDE_FOREVER_VALUE) return true;

  // '7일간 보지 않기' → 저장된 기한 이전이면 숨김
  const hideUntil = new Date(hideValue).getTime();
  return !Number.isNaN(hideUntil) && now < hideUntil;
};

const isInPeriod = (popup: NoticePopup, now: number): boolean => {
  const startAt = new Date(popup.startAt).getTime();
  const endAt = new Date(popup.endAt).getTime();

  if (Number.isNaN(startAt) || Number.isNaN(endAt)) {
    console.error(`팝업 노출 기간 형식 오류 (${popup.id}):`, popup.startAt, popup.endAt);
    return false;
  }

  return now >= startAt && now <= endAt;
};

// 노출 기간 안이고 사용자가 숨기지 않은 팝업만 전달된 순서(우선순위)대로 반환
export const getVisibleNoticePopups = (popups: NoticePopup[]): NoticePopup[] => {
  const now = Date.now();
  return popups.filter((popup) => isInPeriod(popup, now) && !isHiddenByUser(popup, now));
};

type UseNoticePopupsResult = {
  visiblePopups: NoticePopup[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  // 팝업 전체 닫기 (숨김 저장 없음)
  closeAll: () => void;
  // 현재 슬라이드만 7일간 숨김
  hideCurrentFor7Days: () => void;
  // 현재 슬라이드만 영구 숨김
  hideCurrentForever: () => void;
};

// 팝업 후보 목록을 받아 노출 대상 판정과 슬라이드 상태를 관리
const useNoticePopups = (popups: NoticePopup[]): UseNoticePopupsResult => {
  const [visiblePopups, setVisiblePopups] = useState<NoticePopup[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    setVisiblePopups(getVisibleNoticePopups(popups));
    setActiveIndex(0);
  }, [popups]);

  const closeAll = () => {
    setVisiblePopups([]);
    setActiveIndex(0);
  };

  const hideCurrent = (hideValue: string) => {
    const currentPopup = visiblePopups[activeIndex];
    if (!currentPopup) return;

    writeHideValue(currentPopup.storageKey, hideValue);

    const restPopups = visiblePopups.filter((_, index) => index !== activeIndex);
    setVisiblePopups(restPopups);
    setActiveIndex(Math.min(activeIndex, Math.max(restPopups.length - 1, 0)));
  };

  const hideCurrentFor7Days = () => {
    hideCurrent(new Date(Date.now() + 7 * DAY_IN_MS).toISOString());
  };

  const hideCurrentForever = () => {
    hideCurrent(HIDE_FOREVER_VALUE);
  };

  return {
    visiblePopups,
    activeIndex,
    setActiveIndex,
    closeAll,
    hideCurrentFor7Days,
    hideCurrentForever,
  };
};

export default useNoticePopups;
